// Buxton detail-page parser. buxton.com.au is a Next.js app-router site: the
// listing record travels in the React Server Components payload, pushed as
// JSON-escaped strings:
//   self.__next_f.push([1,"…\"listing\":{\"id\":45542,…,\"listing_payload\":{…}}…"])
// Decoded and joined, the payload holds exactly one "listing":{"id":…} object
// (this page's; the related-listing cards sit under "relatedListings" with
// their own prices and sold prices, and are never read). From it:
//   street_address, suburb, address_state, postcode, bedrooms, bathrooms,
//   latitude, longitude, listing_agents[{name, office_name}], and in
//   listing_payload: category ("House"), parking_total, listing_area
//   [{area_type:"Land", area, area_unit:"Square Metre"}],
//   sold_details_sold_date ("2020-12-03"), sold_details_sold_price,
//   sold_details_sold_price_display.
//
// Prices that are NOT the sale price: JSON-LD offers.price and the record's
// price / price_view are the campaign asking price ($3,000,000 on a home the
// record says sold for $3,590,000). The headline ("Landmark Edwardian
// Estate…") is not a property type.
//
// Hidden prices. When sold_details_sold_price_display is false the page shows
// "Undisclosed", but the record still holds sold_details_sold_price. George,
// 2026-10-13: "if there's no public price, use the hidden price" — it is the
// agency's own figure for the sale, and a sale with no price is of no use to
// the valuation. So with no public price, salePrice (and price) is the hidden
// figure, marked salePriceSource "hidden_unverified" (no other source has
// confirmed it, unlike the "hidden_cross_checked" rows the database promotes),
// and hiddenSalePrice keeps the figure for audit. A public price is used as
// is, with no source mark. Either way the figure goes through the
// implausible-price guard.

import { rejectImplausibleSalePrice } from "@repo/adapter-core";
import {
  areaValue,
  count,
  formatAddress,
  htmlText,
  isRecord,
  parseListingDate,
  propertyTypeLabel,
  readJsonObjectAt,
  stateCode,
  text,
} from "../shared/listing-html.ts";

const RSC_PUSH_RE = /self\.__next_f\.push\(\[1,("(?:[^"\\]|\\.)*")\]\)/g;
const LISTING_KEY = '"listing":{"id":';

function readListing(html: string): Record<string, unknown> | null {
  let payload = "";
  for (const match of html.matchAll(RSC_PUSH_RE)) {
    try {
      payload += JSON.parse(match[1]) as string;
    } catch {
      // A chunk that is not a JSON string literal carries nothing we read.
    }
  }
  const at = payload.indexOf(LISTING_KEY);
  if (at < 0) return null;
  return readJsonObjectAt(payload, at + '"listing":'.length);
}

/** "/property/984664/44-rose-street-mckinnon-vic-3204/" → "44-rose-street-mckinnon-vic-3204". */
function addressSlug(url: string): string | null {
  return url.match(/\/property\/\d+\/([a-z0-9-]+)\/?(?:[?#].*)?$/i)?.[1]?.toLowerCase() ?? null;
}

function soldAmountOf(value: unknown): number | null {
  const n = typeof value === "number" ? value : Number(text(value) ?? Number.NaN);
  return Number.isFinite(n) && n >= 10_000 ? Math.round(n) : null;
}

export function parseBuxtonDetailHtml(html: string, url: string): Record<string, unknown> {
  const listing = readListing(html);
  if (!listing) return {};
  // Buxton re-ids listings (a stored /property/154214/… URL serves listing
  // 984664), so match on the address part of the canonical URL instead.
  const canonical = html.match(/<link rel="canonical" href="([^"]+)"/i)?.[1];
  const wanted = addressSlug(url);
  const served = canonical ? addressSlug(canonical) : null;
  if (wanted && served && wanted !== served) return {};

  const street = text(listing.street_address);
  if (!street) return {};
  const suburb = text(listing.suburb);
  const state = stateCode(text(listing.address_state));
  const postcode = text(listing.postcode);
  const payload = isRecord(listing.listing_payload) ? listing.listing_payload : {};

  const sold = text(listing.status)?.toLowerCase() === "sold";
  const hit: Record<string, unknown> = {
    address: formatAddress(street, suburb, state, postcode),
    suburb,
    state,
    postcode,
    listingType: sold ? "sold" : null,
    saleDate: sold ? parseListingDate(text(payload.sold_details_sold_date)) : null,
  };

  const soldPrice = sold ? soldAmountOf(payload.sold_details_sold_price) : null;
  const disclosed = payload.sold_details_sold_price_display === true;
  hit.salePrice = soldPrice;
  if (!disclosed && soldPrice !== null) hit.hiddenSalePrice = soldPrice;
  rejectImplausibleSalePrice(hit);
  hit.price = hit.salePrice;
  if (!disclosed && hit.salePrice !== null) hit.salePriceSource = "hidden_unverified";
  // The visible sold line: <p …>Sold</p><div class="col-span-2 …"><div …><p …>Undisclosed</p>
  const shown = html.match(
    />Sold<\/p><div class="col-span-2[^"]*"><div[^>]*><p[^>]*>([^<]*)<\/p>/i,
  );
  hit.priceDisplay = sold && shown ? text(htmlText(shown[1])) : null;

  hit.bedrooms = count(listing.bedrooms);
  hit.bathrooms = count(listing.bathrooms);
  hit.parking = count(payload.parking_total) ?? count(listing.parking);
  hit.propertyType = propertyTypeLabel(text(payload.category));

  const areas = Array.isArray(payload.listing_area) ? payload.listing_area : [];
  const land = areas.find(
    (a) => isRecord(a) && a.area_type === "Land" && a.area_unit === "Square Metre",
  );
  hit.landSize = isRecord(land) ? areaValue(land.area) : null;
  hit.landSizeUnit = hit.landSize ? "sqm" : null;

  const lat = listing.latitude;
  const lng = listing.longitude;
  if (typeof lat === "number" && typeof lng === "number" && lat !== 0 && lng !== 0) {
    hit.latitude = lat;
    hit.longitude = lng;
  }

  const agents = Array.isArray(listing.listing_agents)
    ? listing.listing_agents.filter(isRecord)
    : [];
  const names = agents.map((agent) => text(agent.name)).filter(Boolean);
  hit.agentName = names.length > 0 ? names.join(", ") : null;
  const office = agents.map((agent) => text(agent.office_name)).find(Boolean);
  hit.agencyName = office ? `Buxton ${office}` : "Buxton";

  return hit;
}
